/**
 * Centralized status style definitions
 *
 * These styles are used across components for consistent status/urgency level styling.
 * All styles include both light and dark mode variants.
 */

/**
 * Status kind styles
 * D: Draft (yellow)
 * I: Initial (blue)
 * T: Transitory (orange)
 */
export const STATUS_KIND_STYLES: Record<'D' | 'I' | 'T', string> = {
  D: 'tw:bg-rose-50 tw:text-rose-800/80 tw:border-rose-500/30 tw:dark:bg-yellow-800/25 tw:dark:text-yellow-200 tw:dark:border-yellow-400/40',
  I: 'tw:bg-blue-50 tw:text-blue-800 tw:border-blue-500/40 tw:dark:bg-orange-800/25 tw:dark:text-orange-200 tw:dark:border-orange-400/40',
  T: 'tw:bg-brand-green-2/50 tw:text-brand-green-0 tw:border-brand-green-1/50 tw:dark:bg-blue-800/25 tw:dark:text-blue-200 tw:dark:border-blue-400/40',
};

/**
 * Final status styles (for statuses with category_kind "F")
 */
export const FINAL_STATUS_STYLES: Record<string, string> = {
  CMPLT:
    'tw:bg-green-100 tw:text-green-800 tw:border-green-500/20 tw:dark:bg-green-800/25 tw:dark:text-green-200 tw:dark:border-green-400/40',
  CLOSED:
    'tw:bg-zinc-100 tw:text-zinc-800 tw:border-zinc-300 tw:dark:bg-zinc-600 tw:dark:text-zinc-200 tw:dark:border-zinc-400',
  ERROR:
    'tw:bg-red-100 tw:text-red-800 tw:border-red-300 tw:dark:bg-red-600 tw:dark:text-red-200 tw:dark:border-red-400',
};

/**
 * Default/fallback status style
 */
export const DEFAULT_STATUS_STYLE =
  'tw:bg-zinc-100 tw:text-zinc-800 tw:border-zinc-300 tw:dark:bg-zinc-600 tw:dark:text-zinc-200 tw:dark:border-zinc-400';

/**
 * Category to kind mapping
 * D: Draft; I: Initial; T: Transitory; F: Final
 */
export const CATEGORY_KINDS: Record<string, 'D' | 'I' | 'T' | 'F'> = {
  DRAFT: 'D',
  NEW: 'I',
  RESUBMIT: 'I',
  IP: 'T',
  LOCKED: 'T',
  HOLD: 'T',
  CMPLT: 'F',
  CLOSED: 'F',
  ERROR: 'F',
};

/**
 * Get status style classes based on category
 * @param category - The status category (e.g., 'DRAFT', 'NEW', 'IP', 'CLOSED')
 * @returns The appropriate style classes for the category
 */
export function getStatusStyle(category?: string): string {
  const categoryKey = category?.toUpperCase() || '';
  const kind = CATEGORY_KINDS[categoryKey];

  return (
    (kind === 'F' && FINAL_STATUS_STYLES[categoryKey]) ||
    (kind && STATUS_KIND_STYLES[kind]) ||
    DEFAULT_STATUS_STYLE
  );
}
